/**
 * Texture atlas.
 *
 * Tiles come from the official Minecraft textures when they have been
 * extracted into `public/mc/` (run `node scripts/extract-minecraft-textures.mjs`
 * on a machine with the game installed — Mojang's assets may be *used* in fan
 * projects but not redistributed, so the extracted files are gitignored and
 * the repo ships none of them). Missing tiles fall back to the procedural
 * painters below.
 *
 * Each 32x32 tile sits in a 36x36 cell whose 2px gutter is filled with wrapped
 * copies of the tile's edges — so the shader can repeat a tile across a
 * greedy-merged quad with linear filtering and no bleeding at tile seams.
 * (Vanilla 16x16 textures are scaled 2x into the 32px tile.)
 *
 * The mesher bakes vegetation tint into vertex colors only for tiles listed in
 * TINTABLE_TILES, so grayscale tiles (grass tops/leaves/plants) get their
 * color from the block tint, while pre-colored tiles (grass side) are not
 * multiplied.
 */

import { COLOR_KEYS, COLOR_TILES, CROP_TILES, ENTITY_TILE_BASE, T } from './blocks';
import { FACE_TILE_TEXELS, FACE_TILES, type FaceTile } from './entityModels';
import { mulberry32 } from './noise';
import { utilityPainters, utilityTileFiles } from './render/utilityTextures';
import {
  ATLAS_COLS, ATLAS_H, ATLAS_ROWS, ATLAS_W, CELL, GUTTER, TILE,
} from './atlasLayout';

export {
  ATLAS_COLS, ATLAS_H, ATLAS_ROWS, ATLAS_W, CELL, GUTTER, TILE,
  tileOriginU, tileOriginV,
} from './atlasLayout';

type Painter = (ctx: CanvasRenderingContext2D, rnd: () => number) => void;

function speckle(ctx: CanvasRenderingContext2D, rnd: () => number, base: [number, number, number], amount: number, count = 380): void {
  const [r, g, b] = base;
  ctx.fillStyle = `rgb(${r},${g},${b})`;
  ctx.fillRect(0, 0, TILE, TILE);
  for (let i = 0; i < count; i++) {
    const d = (rnd() - 0.5) * 2 * amount;
    ctx.fillStyle = rgb(r + d, g + d, b + d);
    ctx.fillRect((rnd() * TILE) | 0, (rnd() * TILE) | 0, 1 + ((rnd() * 2) | 0), 1);
  }
}

/** Wither skeleton skull: charcoal bone with faint cracks (vanilla's wither skeleton is near black, ~#2b2b2b). */
function skullBase(ctx: CanvasRenderingContext2D, rnd: () => number): void {
  speckle(ctx, rnd, [46, 48, 48], 8, 200);
}

function rgb(r: number, g: number, b: number, a = 1): string {
  const c = (v: number) => Math.max(0, Math.min(255, Math.round(v)));
  return `rgba(${c(r)},${c(g)},${c(b)},${a})`;
}

function px(ctx: CanvasRenderingContext2D, x: number, y: number, color: string): void {
  ctx.fillStyle = color;
  ctx.fillRect(x, y, 1, 1);
}

const PAINTERS: Record<number, Painter> = {
  // nether fallbacks (used only when public/mc has not been extracted)
  [T.BLACKSTONE]: (ctx, rnd) => speckle(ctx, rnd, [42, 36, 41], 14),
  [T.BLACKSTONE_TOP]: (ctx, rnd) => speckle(ctx, rnd, [46, 40, 45], 12),
  [T.CRIMSON_NYLIUM]: (ctx, rnd) => speckle(ctx, rnd, [130, 31, 31], 30),
  [T.CRIMSON_NYLIUM_SIDE]: (ctx, rnd) => { speckle(ctx, rnd, [97, 38, 38], 22); ctx.fillStyle = 'rgb(130,31,31)'; ctx.fillRect(0, 0, TILE, 7); },
  [T.WARPED_NYLIUM]: (ctx, rnd) => speckle(ctx, rnd, [43, 114, 101], 26),
  [T.WARPED_NYLIUM_SIDE]: (ctx, rnd) => { speckle(ctx, rnd, [97, 38, 38], 22); ctx.fillStyle = 'rgb(43,114,101)'; ctx.fillRect(0, 0, TILE, 7); },
  [T.CRIMSON_STEM]: (ctx, rnd) => speckle(ctx, rnd, [92, 25, 29], 24),
  [T.CRIMSON_STEM_TOP]: (ctx, rnd) => speckle(ctx, rnd, [112, 49, 70], 20),
  [T.WARPED_STEM]: (ctx, rnd) => speckle(ctx, rnd, [58, 58, 77], 24),
  [T.WARPED_STEM_TOP]: (ctx, rnd) => speckle(ctx, rnd, [53, 109, 110], 20),
  [T.NETHER_WART_BLOCK]: (ctx, rnd) => speckle(ctx, rnd, [115, 2, 2], 24),
  [T.WARPED_WART_BLOCK]: (ctx, rnd) => speckle(ctx, rnd, [22, 119, 121], 24),
  [T.NETHER_PORTAL]: (ctx, rnd) => speckle(ctx, rnd, [88, 12, 190], 50, 600),
  // the End (used only when public/mc has not been extracted; the portal has no vanilla block texture)
  [T.END_STONE]: (ctx, rnd) => speckle(ctx, rnd, [219, 222, 158], 14),
  [T.END_STONE_BRICKS]: (ctx, rnd) => {
    speckle(ctx, rnd, [225, 230, 170], 10);
    ctx.fillStyle = 'rgb(176,180,125)';
    for (let k = 0; k < TILE; k += 16) { ctx.fillRect(0, k, TILE, 1); ctx.fillRect(((k >> 4) & 1) * 8 + 8, k, 1, 16); }
  },
  [T.END_FRAME_TOP]: (ctx, rnd) => {
    speckle(ctx, rnd, [48, 90, 74], 10);
    ctx.fillStyle = 'rgb(219,222,158)'; ctx.fillRect(0, 0, TILE, 3); ctx.fillRect(0, TILE - 3, TILE, 3);
    ctx.fillRect(0, 0, 3, TILE); ctx.fillRect(TILE - 3, 0, 3, TILE);
    ctx.fillStyle = 'rgb(18,40,34)'; ctx.fillRect(8, 8, 16, 16);
  },
  [T.END_FRAME_SIDE]: (ctx, rnd) => {
    speckle(ctx, rnd, [219, 222, 158], 12);
    ctx.fillStyle = 'rgb(48,90,74)'; ctx.fillRect(0, 0, TILE, 5);
  },
  [T.END_FRAME_EYE]: (ctx, rnd) => { speckle(ctx, rnd, [32, 112, 96], 12); ctx.fillStyle = 'rgb(10,10,12)'; ctx.fillRect(12, 12, 8, 8); },
  [T.END_PORTAL]: (ctx, rnd) => {
    speckle(ctx, rnd, [6, 12, 16], 6, 200);
    for (let i = 0; i < 40; i++) {
      const v = 90 + ((rnd() * 140) | 0);
      ctx.fillStyle = rgb(v * 0.5, v, v * 0.9);
      ctx.fillRect((rnd() * TILE) | 0, (rnd() * TILE) | 0, 1, 1);
    }
  },
  [T.DRAGON_EGG]: (ctx, rnd) => {
    speckle(ctx, rnd, [14, 8, 20], 8);
    for (let i = 0; i < 26; i++) { ctx.fillStyle = rgb(70 + rnd() * 40, 20, 110 + rnd() * 40); ctx.fillRect((rnd() * TILE) | 0, (rnd() * TILE) | 0, 2, 2); }
  },
  // wither skeleton skull (block-entity in vanilla, so no block texture exists): the 8x8x8 head sits in
  // texels x 4..11 of the cell and (side faces) y 8..15, so the art lives in that window of the 16-grid
  [T.WITHER_SKULL_SIDE]: (ctx, rnd) => skullBase(ctx, rnd),
  [T.WITHER_SKULL_TOP]: (ctx, rnd) => { skullBase(ctx, rnd); const k = TILE / 16; ctx.fillStyle = 'rgb(22,22,24)'; ctx.fillRect(7 * k, 4 * k, 2 * k, 8 * k); },
  [T.WITHER_SKULL_FACE]: (ctx, rnd) => {
    skullBase(ctx, rnd);
    const k = TILE / 16, r = (x: number, y: number, w: number, h: number, c: string): void => { ctx.fillStyle = c; ctx.fillRect(x * k, y * k, w * k, h * k); };
    r(5, 10, 2, 2, 'rgb(8,8,10)'); r(9, 10, 2, 2, 'rgb(8,8,10)'); // eye sockets
    r(7, 12, 2, 2, 'rgb(14,14,16)'); // nose
    r(5, 14, 6, 1, 'rgb(14,14,16)'); for (let x = 5; x < 11; x += 2) r(x, 14, 1, 2, 'rgb(48,50,50)'); // teeth
  },
  // structures round 2 (procedural fallbacks; the official tiles replace them when extracted)
  [T.IRON_BARS]: (ctx) => {
    ctx.clearRect(0, 0, TILE, TILE);
    ctx.fillStyle = 'rgb(120,122,124)'; ctx.fillRect(14, 0, 4, TILE);
    ctx.fillStyle = 'rgb(160,162,165)'; ctx.fillRect(14, 0, 1, TILE);
    ctx.fillStyle = 'rgb(86,88,92)'; ctx.fillRect(17, 0, 1, TILE);
  },
  [T.POLISHED_BLACKSTONE_BRICKS]: (ctx, rnd) => {
    speckle(ctx, rnd, [44, 40, 48], 6);
    ctx.fillStyle = 'rgb(24,22,28)';
    for (let y = 0; y < TILE; y += 8) ctx.fillRect(0, y, TILE, 1);
    for (let y = 0; y < TILE; y += 16) { ctx.fillRect(8, y, 1, 8); ctx.fillRect(24, y, 1, 8); }
    for (let y = 8; y < TILE; y += 16) { ctx.fillRect(0, y, 1, 8); ctx.fillRect(16, y, 1, 8); }
  },
  [T.GILDED_BLACKSTONE]: (ctx, rnd) => { speckle(ctx, rnd, [50, 44, 52], 10); oreBlobs(ctx, rnd, [235, 190, 60], 7); },
  [T.TRIAL_SPAWNER]: (ctx, rnd) => {
    speckle(ctx, rnd, [74, 66, 60], 8);
    ctx.fillStyle = 'rgb(176,112,70)'; ctx.fillRect(0, 0, TILE, 3); ctx.fillRect(0, TILE - 3, TILE, 3);
    ctx.fillStyle = 'rgb(40,150,200)'; ctx.fillRect(10, 10, 12, 12);
  },
  [T.VAULT]: (ctx, rnd) => {
    speckle(ctx, rnd, [70, 62, 56], 8);
    ctx.fillStyle = 'rgb(150,100,66)'; ctx.fillRect(0, 0, TILE, 2); ctx.fillRect(0, TILE - 2, TILE, 2);
    ctx.fillStyle = 'rgb(20,18,22)'; ctx.fillRect(12, 10, 8, 12);
    ctx.fillStyle = 'rgb(200,170,80)'; ctx.fillRect(15, 14, 2, 4);
  },
  [T.CHORUS_PLANT]: (ctx, rnd) => speckle(ctx, rnd, [96, 62, 100], 14),
  [T.CHORUS_FLOWER]: (ctx, rnd) => { speckle(ctx, rnd, [150, 118, 156], 12); ctx.fillStyle = 'rgb(90,58,96)'; ctx.fillRect(4, 4, 24, 2); ctx.fillRect(4, 26, 24, 2); },
  [T.PURPUR]: (ctx, rnd) => speckle(ctx, rnd, [169, 125, 169], 10),
  [T.PURPUR_PILLAR]: (ctx, rnd) => { speckle(ctx, rnd, [172, 129, 172], 8); ctx.fillStyle = 'rgb(140,100,140)'; ctx.fillRect(8, 0, 2, TILE); ctx.fillRect(22, 0, 2, TILE); },
  [T.NETHER_GOLD_ORE]: (ctx, rnd) => { speckle(ctx, rnd, [97, 38, 38], 22); oreBlobs(ctx, rnd, [230, 180, 50], 6); },
  [T.GRASS_TOP]: (ctx, rnd) => speckle(ctx, rnd, [150, 150, 150], 42, 500),
  [T.DIRT]: (ctx, rnd) => speckle(ctx, rnd, [134, 96, 67], 26),
  [T.STONE]: (ctx, rnd) => speckle(ctx, rnd, [125, 125, 125], 22),
  [T.COBBLE]: (ctx, rnd) => {
    speckle(ctx, rnd, [110, 110, 110], 14);
    // irregular stones
    for (let gy = 0; gy < 4; gy++) {
      for (let gx = 0; gx < 4; gx++) {
        const ox = gx * 8 + ((rnd() * 2) | 0);
        const oy = gy * 8 + ((rnd() * 2) | 0);
        const w = 5 + ((rnd() * 3) | 0);
        const h = 5 + ((rnd() * 3) | 0);
        const v = 95 + ((rnd() * 50) | 0);
        ctx.fillStyle = rgb(v, v, v);
        ctx.fillRect(ox, oy, w, h);
        ctx.fillStyle = rgb(v - 35, v - 35, v - 35);
        ctx.fillRect(ox + w - 1, oy, 1, h);
        ctx.fillRect(ox, oy + h - 1, w, 1);
      }
    }
  },
  [T.SAND]: (ctx, rnd) => speckle(ctx, rnd, [219, 207, 163], 18),
  [T.GRAVEL]: (ctx, rnd) => {
    speckle(ctx, rnd, [130, 124, 120], 30, 300);
    for (let i = 0; i < 40; i++) {
      const v = 70 + ((rnd() * 120) | 0);
      ctx.fillStyle = rgb(v, v - 8, v - 12);
      ctx.fillRect((rnd() * TILE) | 0, (rnd() * TILE) | 0, 2 + ((rnd() * 3) | 0), 2);
    }
  },
  [T.LOG_SIDE]: (ctx, rnd) => {
    speckle(ctx, rnd, [102, 81, 50], 14);
    for (let x = 0; x < TILE; x += 2 + ((rnd() * 3) | 0)) {
      const v = -25 + rnd() * 18;
      ctx.fillStyle = rgb(102 + v, 81 + v, 50 + v);
      ctx.fillRect(x, 0, 1, TILE);
    }
  },
  [T.LOG_TOP]: (ctx, rnd) => {
    speckle(ctx, rnd, [154, 125, 77], 12);
    ctx.strokeStyle = rgb(102, 81, 50);
    for (let r = 3; r <= 13; r += 4) {
      ctx.strokeRect(16 - r, 16 - r, r * 2, r * 2);
    }
    ctx.fillStyle = rgb(102, 81, 50);
    ctx.fillRect(15, 15, 2, 2);
  },
  [T.LEAVES]: (ctx, rnd) => {
    // Minecraft "fancy graphics" leaves: a dense, clumped leaf mass with a
    // few organic transparent gaps — not scattered noise. Painted grayscale
    // (TINTABLE_TILES) so the mesher tints it. Everything wraps modulo TILE
    // so the tile repeats seamlessly across merged quads.
    const wrap = (v: number) => ((v % TILE) + TILE) % TILE;
    ctx.clearRect(0, 0, TILE, TILE);

    // base: solid mid-gray foliage
    for (let y = 0; y < TILE; y++) {
      for (let x = 0; x < TILE; x++) {
        const v = 138 + ((rnd() * 26) | 0);
        px(ctx, x, y, rgb(v, v, v));
      }
    }
    // clumped clusters: brighter leaf bundles and darker crevices between them
    for (let i = 0; i < 26; i++) {
      const cx = (rnd() * TILE) | 0;
      const cy = (rnd() * TILE) | 0;
      const s = 2 + ((rnd() * 3) | 0);
      const hi = rnd() < 0.5;
      for (let dy = 0; dy < s; dy++) {
        for (let dx = 0; dx < s; dx++) {
          if (rnd() < 0.3) continue;
          const v = hi ? 168 + ((rnd() * 52) | 0) : 96 + ((rnd() * 34) | 0);
          px(ctx, wrap(cx + dx), wrap(cy + dy), rgb(v, v, v));
        }
      }
    }
    // organic transparent gaps (~15%): small wrapped clear clusters
    for (let i = 0; i < 58; i++) {
      const cx = (rnd() * TILE) | 0;
      const cy = (rnd() * TILE) | 0;
      const s = 1 + ((rnd() * 3) | 0);
      for (let dy = 0; dy < s; dy++) {
        for (let dx = 0; dx < s; dx++) {
          ctx.clearRect(wrap(cx + dx), wrap(cy + dy), 1, 1);
        }
      }
    }
    // darken pixels bordering a hole — fakes depth inside the canopy
    const img = ctx.getImageData(0, 0, TILE, TILE);
    const d = img.data;
    const alphaAt = (x: number, y: number) => d[(wrap(y) * TILE + wrap(x)) * 4 + 3];
    for (let y = 0; y < TILE; y++) {
      for (let x = 0; x < TILE; x++) {
        const i = (y * TILE + x) * 4;
        if (d[i + 3] === 0) continue;
        const holeNear =
          alphaAt(x, y - 1) === 0 || alphaAt(x, y + 1) === 0 ||
          alphaAt(x - 1, y) === 0 || alphaAt(x + 1, y) === 0;
        if (holeNear) {
          d[i] = (d[i] * 0.7) | 0;
          d[i + 1] = (d[i + 1] * 0.7) | 0;
          d[i + 2] = (d[i + 2] * 0.7) | 0;
        }
      }
    }
    ctx.putImageData(img, 0, 0);
  },
  [T.PLANKS]: (ctx, rnd) => {
    speckle(ctx, rnd, [162, 130, 78], 14);
    for (let y = 7; y < TILE; y += 8) {
      ctx.fillStyle = rgb(110, 88, 52);
      ctx.fillRect(0, y, TILE, 1);
    }
    for (let i = 0; i < 10; i++) {
      const y = ((rnd() * 4) | 0) * 8 + ((rnd() * 6) | 0);
      const x = (rnd() * TILE) | 0;
      ctx.fillStyle = rgb(120, 96, 58);
      ctx.fillRect(x, y, 2, 1);
    }
  },
  [T.GLASS]: (ctx) => {
    ctx.clearRect(0, 0, TILE, TILE);
    ctx.fillStyle = rgb(210, 235, 240, 0.16);
    ctx.fillRect(0, 0, TILE, TILE);
    ctx.fillStyle = rgb(225, 240, 245);
    ctx.fillRect(0, 0, TILE, 1);
    ctx.fillRect(0, TILE - 1, TILE, 1);
    ctx.fillRect(0, 0, 1, TILE);
    ctx.fillRect(TILE - 1, 0, 1, TILE);
    ctx.fillStyle = rgb(255, 255, 255, 0.5);
    for (let i = 0; i < 5; i++) {
      const x = 4 + i * 5;
      ctx.fillRect(x, 6 + i * 3, 1, 6);
    }
  },
  [T.WATER]: (ctx, rnd) => {
    ctx.fillStyle = rgb(40, 92, 180, 0.82);
    ctx.fillRect(0, 0, TILE, TILE);
    for (let i = 0; i < 90; i++) {
      ctx.fillStyle = rgb(70 + rnd() * 40, 130 + rnd() * 40, 220, 0.28);
      ctx.fillRect((rnd() * TILE) | 0, (rnd() * TILE) | 0, 3 + ((rnd() * 5) | 0), 1);
    }
  },
  [T.SNOW]: (ctx, rnd) => speckle(ctx, rnd, [242, 246, 250], 10, 300),
  [T.SNOW_SIDE]: (ctx, rnd) => {
    PAINTERS[T.DIRT](ctx, rnd);
    ctx.fillStyle = rgb(242, 246, 250);
    ctx.fillRect(0, 0, TILE, 7);
    for (let x = 0; x < TILE; x++) {
      if (rnd() < 0.4) px(ctx, x, 7, rgb(242, 246, 250));
      if (rnd() < 0.15) px(ctx, x, 8, rgb(242, 246, 250));
    }
  },
  [T.BEDROCK]: (ctx, rnd) => {
    speckle(ctx, rnd, [70, 70, 70], 34, 600);
    for (let i = 0; i < 30; i++) {
      const v = 30 + ((rnd() * 60) | 0);
      ctx.fillStyle = rgb(v, v, v);
      ctx.fillRect((rnd() * TILE) | 0, (rnd() * TILE) | 0, 3, 3);
    }
  },
  [T.COAL]: (ctx, rnd) => {
    PAINTERS[T.STONE](ctx, rnd);
    oreBlobs(ctx, rnd, [28, 28, 30], 4);
  },
  [T.IRON]: (ctx, rnd) => {
    PAINTERS[T.STONE](ctx, rnd);
    oreBlobs(ctx, rnd, [206, 161, 128], 4);
  },
  [T.GOLD]: (ctx, rnd) => {
    PAINTERS[T.STONE](ctx, rnd);
    oreBlobs(ctx, rnd, [246, 208, 61], 4);
  },
  [T.DIAMOND]: (ctx, rnd) => {
    PAINTERS[T.STONE](ctx, rnd);
    oreBlobs(ctx, rnd, [93, 236, 229], 4);
  },
  [T.GLOWSTONE]: (ctx, rnd) => {
    speckle(ctx, rnd, [144, 110, 60], 20);
    for (let i = 0; i < 46; i++) {
      ctx.fillStyle = rgb(250, 220, 110 + rnd() * 60);
      ctx.fillRect((rnd() * TILE) | 0, (rnd() * TILE) | 0, 2 + ((rnd() * 3) | 0), 2 + ((rnd() * 2) | 0));
    }
  },
  [T.TALL_GRASS]: (ctx, rnd) => {
    ctx.clearRect(0, 0, TILE, TILE);
    for (let i = 0; i < 26; i++) {
      const x = 1 + ((rnd() * (TILE - 2)) | 0);
      const h = 6 + ((rnd() * 18) | 0);
      const v = 120 + ((rnd() * 80) | 0);
      const bend = rnd() < 0.5 ? 1 : -1;
      for (let y = 0; y < h; y++) {
        px(ctx, x + (y > h / 2 ? bend : 0), TILE - 1 - y, rgb(v, v, v));
      }
    }
  },
  [T.POPPY]: (ctx, rnd) => {
    ctx.clearRect(0, 0, TILE, TILE);
    ctx.fillStyle = rgb(58, 110, 40);
    ctx.fillRect(15, 14, 2, 18);
    ctx.fillRect(11, 20, 4, 1);
    ctx.fillRect(17, 24, 4, 1);
    ctx.fillStyle = rgb(200, 40, 40);
    ctx.fillRect(12, 6, 8, 8);
    ctx.fillStyle = rgb(235, 80, 70);
    ctx.fillRect(13, 7, 6, 6);
    ctx.fillStyle = rgb(40, 30, 20);
    ctx.fillRect(15, 9, 2, 2);
    for (let i = 0; i < 6; i++) px(ctx, 10 + ((rnd() * 12) | 0), 4 + ((rnd() * 12) | 0), rgb(200, 40, 40));
  },
  [T.CACTUS_SIDE]: (ctx, rnd) => {
    speckle(ctx, rnd, [58, 118, 48], 14);
    ctx.fillStyle = rgb(40, 88, 34);
    for (let x = 0; x < TILE; x += 8) ctx.fillRect(x, 0, 2, TILE);
    ctx.fillStyle = rgb(220, 230, 200);
    for (let i = 0; i < 12; i++) {
      const x = (rnd() * TILE) | 0;
      const y = (rnd() * TILE) | 0;
      ctx.fillRect(x, y, 1, 2);
    }
  },
  [T.CACTUS_TOP]: (ctx, rnd) => {
    speckle(ctx, rnd, [70, 132, 58], 12);
    ctx.strokeStyle = rgb(40, 88, 34);
    ctx.strokeRect(4, 4, TILE - 8, TILE - 8);
  },
  [T.STONE_BRICK]: (ctx, rnd) => {
    speckle(ctx, rnd, [122, 122, 122], 16);
    ctx.fillStyle = rgb(88, 88, 88);
    for (let y = 0; y < TILE; y += 8) ctx.fillRect(0, y, TILE, 1);
    const offset = (y: number) => (y / 8) % 2 === 0 ? 0 : 8;
    for (let y = 0; y < TILE; y += 8) {
      const off = offset(y);
      for (let x = off; x < TILE; x += 16) ctx.fillRect(x, y, 1, 8);
    }
  },
  [T.GRASS_SIDE]: (ctx, rnd) => {
    PAINTERS[T.DIRT](ctx, rnd);
    // pre-colored to match the grass tint (0.55, 0.85, 0.42) * gray
    for (let x = 0; x < TILE; x++) {
      const h = 5 + ((rnd() * 4) | 0);
      for (let y = 0; y < h; y++) {
        const v = 140 + ((rnd() * 50) | 0);
        px(ctx, x, y, rgb(v * 0.55, v * 0.85, v * 0.42));
      }
    }
  },
  [T.TORCH]: (ctx) => {
    ctx.clearRect(0, 0, TILE, TILE);
    // warm halo (semi-transparent, behind everything)
    ctx.fillStyle = rgb(255, 170, 60, 0.3);
    ctx.fillRect(11, 4, 11, 12);
    // stick
    ctx.fillStyle = rgb(116, 86, 50);
    ctx.fillRect(15, 12, 3, 20);
    ctx.fillStyle = rgb(88, 64, 36);
    ctx.fillRect(17, 12, 1, 20);
    // ember head + hot core
    ctx.fillStyle = rgb(255, 214, 110);
    ctx.fillRect(14, 8, 5, 5);
    ctx.fillStyle = rgb(255, 246, 206);
    ctx.fillRect(15, 9, 3, 3);
  },
  [T.LAVA]: (ctx, rnd) => {
    speckle(ctx, rnd, [207, 88, 26], 30);
    for (let i = 0; i < 50; i++) {
      ctx.fillStyle = rgb(250, 170 + rnd() * 60, 40, 0.9);
      ctx.fillRect((rnd() * TILE) | 0, (rnd() * TILE) | 0, 2 + ((rnd() * 4) | 0), 1 + ((rnd() * 3) | 0));
    }
    for (let i = 0; i < 24; i++) {
      ctx.fillStyle = rgb(255, 240, 120, 0.85);
      ctx.fillRect((rnd() * TILE) | 0, (rnd() * TILE) | 0, 1 + ((rnd() * 2) | 0), 1);
    }
  },
  // --- ground cover, seen from above (flat quads) ---
  [T.PINK_PETALS]: (ctx, rnd) => {
    ctx.clearRect(0, 0, TILE, TILE);
    const pinks = [rgb(249, 192, 220), rgb(244, 165, 203), rgb(251, 210, 230), rgb(232, 143, 187)];
    scatterFlowers(ctx, rnd, 3, (i) => ({
      r: 3, petal: pinks[i % 3], hi: pinks[2], core: i % 2 === 0 ? pinks[3] : rgb(214, 106, 160),
    }));
  },
  [T.WILDFLOWERS]: (ctx, rnd) => {
    ctx.clearRect(0, 0, TILE, TILE);
    scatterFlowers(ctx, rnd, 2, (i) => i % 2 === 0
      ? { r: 2.4, petal: rgb(250, 212, 64), hi: rgb(255, 234, 130), core: rgb(226, 146, 38) }
      : { r: 2.4, petal: rgb(240, 240, 232), hi: rgb(255, 255, 255), core: rgb(246, 206, 72) });
  },
  [T.LEAF_LITTER]: (ctx, rnd) => {
    ctx.clearRect(0, 0, TILE, TILE);
    const browns: [number, number, number][] = [[142, 84, 40], [176, 104, 44], [196, 128, 52], [110, 66, 34], [160, 92, 36]];
    for (let i = 0; i < 9; i++) {
      // an oval leaf at a random angle with a darker midrib
      const cx = 4 + rnd() * (TILE - 8);
      const cy = 4 + rnd() * (TILE - 8);
      const a = rnd() * Math.PI;
      const ca = Math.cos(a), sa = Math.sin(a);
      const len = 2.6 + rnd() * 1.2;
      const wid = 1.4 + rnd() * 0.6;
      const [r, g, b] = browns[(rnd() * browns.length) | 0];
      for (let dy = -4; dy <= 4; dy++) {
        for (let dx = -4; dx <= 4; dx++) {
          const u = dx * ca + dy * sa;
          const v = -dx * sa + dy * ca;
          if ((u * u) / (len * len) + (v * v) / (wid * wid) > 1) continue;
          const d = Math.abs(v) < 0.5 ? -28 : (rnd() - 0.5) * 22;
          px(ctx, Math.round(cx + dx), Math.round(cy + dy), rgb(r + d, g + d, b + d));
        }
      }
    }
  },
  [T.LILY_PAD]: (ctx, rnd) => {
    // grayscale (TINTABLE_TILES): the mesher multiplies in vanilla's 0x208030
    ctx.clearRect(0, 0, TILE, TILE);
    const c = TILE / 2 - 0.5;
    for (let y = 0; y < TILE; y++) {
      for (let x = 0; x < TILE; x++) {
        const dx = x - c, dy = y - c;
        const d = Math.hypot(dx, dy);
        if (d > 13.5) continue;
        // the notch: a wedge cut from the rim towards the centre
        if (dy > 2 && Math.abs(dx) < (dy - 2) * 0.32) continue;
        const ang = Math.atan2(dy, dx);
        const vein = Math.abs(Math.sin(ang * 4)) < 0.16 && d > 2.5;
        const v = d > 12 ? 150 : vein ? 232 : 196 + ((rnd() * 22) | 0) - d * 1.2;
        px(ctx, x, y, rgb(v, v, v));
      }
    }
  },

  // --- worldgen feature / structure blocks -------------------------------------
  [T.KELP]: (ctx, rnd) => {
    ctx.clearRect(0, 0, TILE, TILE);
    for (let y = 0; y < TILE; y++) {
      const cx = 16 + Math.round(Math.sin(y * 0.4) * 3);
      for (let x = cx - 4; x <= cx + 4; x++) {
        const v = rnd() * 30;
        px(ctx, x, y, Math.abs(x - cx) < 2 ? rgb(52 + v, 96 + v, 34) : rgb(72 + v, 128 + v, 40));
      }
    }
  },
  // the stalk below the tip: the same fronds with the stem running through
  [T.KELP_PLANT]: (ctx, rnd) => {
    PAINTERS[T.KELP](ctx, rnd);
    for (let y = 0; y < TILE; y++) px(ctx, 15, y, rgb(46, 84, 30));
  },
  [T.SEAGRASS]: (ctx, rnd) => {
    ctx.clearRect(0, 0, TILE, TILE);
    for (let i = 0; i < 12; i++) {
      const x = 3 + ((rnd() * 26) | 0), h = 12 + ((rnd() * 18) | 0), g = 110 + rnd() * 60;
      for (let y = 0; y < h; y++) px(ctx, x + Math.round(Math.sin(y * 0.3 + i) * 1.5), TILE - 1 - y, rgb(30, g, 40));
    }
  },
  [T.SUGAR_CANE]: (ctx, rnd) => {
    ctx.clearRect(0, 0, TILE, TILE);
    for (const x of [6, 15, 24]) {
      for (let y = 0; y < TILE; y++) {
        const joint = y % 8 === 0;
        ctx.fillStyle = joint ? rgb(104, 140, 70) : rgb(140 + rnd() * 20, 190 + rnd() * 20, 100);
        ctx.fillRect(x, y, 3, 1);
      }
      ctx.fillStyle = rgb(110, 170, 80);
      ctx.fillRect(x + 3, 6 + ((rnd() * 16) | 0), 4, 1);
    }
  },
  [T.BAMBOO]: (ctx, rnd) => {
    ctx.clearRect(0, 0, TILE, TILE);
    for (let y = 0; y < TILE; y++) {
      ctx.fillStyle = y % 10 === 0 ? rgb(70, 110, 20) : rgb(96 + rnd() * 16, 150 + rnd() * 16, 28);
      ctx.fillRect(14, y, 4, 1);
    }
    ctx.fillStyle = rgb(86, 150, 36);
    for (const [x, y, w] of [[18, 6, 8], [6, 12, 8], [18, 20, 7], [7, 26, 7]]) {
      ctx.fillRect(x, y, w, 2);
      ctx.fillRect(x + (x > 14 ? w - 3 : 0), y + 2, 3, 2);
    }
  },
  [T.SWEET_BERRY_BUSH]: (ctx, rnd) => {
    ctx.clearRect(0, 0, TILE, TILE);
    for (let i = 0; i < 150; i++) {
      const x = 3 + ((rnd() * 26) | 0), y = 8 + ((rnd() * 24) | 0);
      ctx.fillStyle = rgb(40 + rnd() * 20, 90 + rnd() * 40, 40);
      ctx.fillRect(x, y, 2, 2);
    }
    for (let i = 0; i < 7; i++) {
      ctx.fillStyle = rgb(190, 30, 40);
      ctx.fillRect(5 + ((rnd() * 22) | 0), 10 + ((rnd() * 18) | 0), 2, 2);
    }
  },
  [T.RED_MUSHROOM_BLOCK]: (ctx, rnd) => {
    speckle(ctx, rnd, [196, 34, 30], 14);
    ctx.fillStyle = rgb(240, 236, 230);
    for (let i = 0; i < 5; i++) ctx.fillRect(2 + ((rnd() * 24) | 0), 2 + ((rnd() * 24) | 0), 4 + ((rnd() * 3) | 0), 4 + ((rnd() * 3) | 0));
  },
  [T.BROWN_MUSHROOM_BLOCK]: (ctx, rnd) => speckle(ctx, rnd, [150, 112, 82], 12),
  [T.MUSHROOM_STEM]: (ctx, rnd) => {
    speckle(ctx, rnd, [206, 202, 190], 10);
    ctx.fillStyle = rgb(186, 182, 170);
    for (let x = 3; x < TILE; x += 6) ctx.fillRect(x, 0, 1, TILE);
  },
  [T.SCULK]: (ctx, rnd) => {
    speckle(ctx, rnd, [13, 30, 38], 8);
    for (let i = 0; i < 26; i++) px(ctx, (rnd() * TILE) | 0, (rnd() * TILE) | 0, rgb(20, 150 + rnd() * 70, 170));
  },
  // sculk shrieker / catalyst (vanilla colours: dark sculk teal, bone-white teeth, cyan glow)
  [T.SHRIEKER_TOP]: (ctx, rnd) => {
    speckle(ctx, rnd, [18, 34, 42], 8);
    ctx.fillStyle = rgb(8, 16, 22); ctx.fillRect(8, 8, 16, 16);
    ctx.fillStyle = rgb(210, 205, 180); for (let i = 0; i < 4; i++) { ctx.fillRect(9 + i * 4, 8, 2, 3); ctx.fillRect(9 + i * 4, 21, 2, 3); }
  },
  [T.SHRIEKER_SUMMON_TOP]: (ctx, rnd) => {
    speckle(ctx, rnd, [18, 34, 42], 8);
    ctx.fillStyle = rgb(8, 16, 22); ctx.fillRect(8, 8, 16, 16);
    ctx.fillStyle = rgb(40, 190, 200); ctx.fillRect(12, 12, 8, 8);
    ctx.fillStyle = rgb(210, 205, 180); for (let i = 0; i < 4; i++) { ctx.fillRect(9 + i * 4, 8, 2, 3); ctx.fillRect(9 + i * 4, 21, 2, 3); }
  },
  [T.SHRIEKER_SIDE]: (ctx, rnd) => {
    speckle(ctx, rnd, [20, 40, 50], 8);
    ctx.fillStyle = rgb(210, 205, 180); for (let i = 0; i < 5; i++) ctx.fillRect(3 + i * 6, 0, 3, 5 + (i & 1) * 2);
    for (let i = 0; i < 18; i++) px(ctx, (rnd() * TILE) | 0, 8 + ((rnd() * 24) | 0), rgb(20, 150 + rnd() * 70, 170));
  },
  [T.SHRIEKER_BOTTOM]: (ctx, rnd) => speckle(ctx, rnd, [14, 26, 32], 6),
  [T.CATALYST_TOP]: (ctx, rnd) => {
    speckle(ctx, rnd, [14, 30, 38], 8);
    ctx.fillStyle = rgb(30, 150, 160); ctx.fillRect(6, 6, 20, 20);
    ctx.fillStyle = rgb(140, 230, 230); for (let i = 0; i < 8; i++) px(ctx, 7 + ((rnd() * 18) | 0), 7 + ((rnd() * 18) | 0), rgb(150, 235, 235));
  },
  [T.CATALYST_SIDE]: (ctx, rnd) => {
    speckle(ctx, rnd, [16, 36, 44], 8);
    ctx.fillStyle = rgb(210, 205, 180); ctx.fillRect(0, 0, TILE, 3);
    ctx.fillStyle = rgb(30, 150, 160); ctx.fillRect(0, 3, TILE, 2);
    for (let i = 0; i < 14; i++) px(ctx, (rnd() * TILE) | 0, 6 + ((rnd() * 26) | 0), rgb(20, 150 + rnd() * 70, 170));
  },
  [T.CATALYST_BOTTOM]: (ctx, rnd) => speckle(ctx, rnd, [14, 26, 32], 6),
  [T.CAVE_VINES]: (ctx, rnd) => {
    ctx.clearRect(0, 0, TILE, TILE);
    for (const x of [9, 16, 23]) {
      for (let y = 0; y < TILE; y++) px(ctx, x + Math.round(Math.sin(y * 0.35 + x) * 1.5), y, rgb(70, 120 + rnd() * 40, 40));
      for (let k = 0; k < 3; k++) {
        const y = 4 + ((rnd() * 24) | 0);
        ctx.fillStyle = rgb(110, 150, 50);
        ctx.fillRect(x - 3, y, 6, 2);
      }
    }
    for (let i = 0; i < 4; i++) {
      const x = 6 + ((rnd() * 18) | 0), y = 8 + ((rnd() * 18) | 0);
      ctx.fillStyle = rgb(250, 170, 40);
      ctx.fillRect(x, y, 4, 4);
      ctx.fillStyle = rgb(255, 230, 120);
      ctx.fillRect(x + 1, y + 1, 2, 2);
    }
  },
  [T.DRIPSTONE_UP]: (ctx, rnd) => {
    ctx.clearRect(0, 0, TILE, TILE);
    for (let y = 0; y < TILE; y++) {
      const w = Math.max(1, Math.round((y / TILE) * 8));
      for (let x = 16 - w; x < 16 + w; x++) px(ctx, x, y, rgb(134 + rnd() * 20, 107 + rnd() * 16, 92 + rnd() * 14));
    }
  },
  [T.DRIPSTONE_DOWN]: (ctx, rnd) => {
    ctx.clearRect(0, 0, TILE, TILE);
    for (let y = 0; y < TILE; y++) {
      const w = Math.max(1, Math.round((1 - y / TILE) * 8));
      for (let x = 16 - w; x < 16 + w; x++) px(ctx, x, y, rgb(134 + rnd() * 20, 107 + rnd() * 16, 92 + rnd() * 14));
    }
  },
  [T.SMOOTH_BASALT]: (ctx, rnd) => speckle(ctx, rnd, [72, 72, 78], 8),
  [T.POWDER_SNOW]: (ctx, rnd) => speckle(ctx, rnd, [248, 252, 252], 6, 500),
  [T.CORAL_TUBE]: (ctx, rnd) => speckle(ctx, rnd, [49, 87, 206], 26, 500),
  [T.CORAL_BRAIN]: (ctx, rnd) => speckle(ctx, rnd, [207, 91, 159], 26, 500),
  [T.CORAL_BUBBLE]: (ctx, rnd) => speckle(ctx, rnd, [165, 26, 162], 26, 500),
  [T.CORAL_FIRE]: (ctx, rnd) => speckle(ctx, rnd, [163, 35, 46], 26, 500),
  [T.CORAL_HORN]: (ctx, rnd) => speckle(ctx, rnd, [216, 199, 66], 26, 500),
  // Fire overlay: a mostly-transparent tile with orange/yellow flames in the
  // bottom 14 rows and a hot core.  Vanilla's fire texture is 16x16 with the
  // top 2 rows opaque (cuts off cleanly when the slab is 1 px tall).  When no
  // MC asset is loaded we fall back to a procedural painter.
  [T.FIRE_0]: (ctx, rnd) => {
    ctx.clearRect(0, 0, TILE, TILE);
    // outer flame: orange
    for (let i = 0; i < 36; i++) {
      const x = (rnd() * TILE) | 0;
      const h = 6 + ((rnd() * 12) | 0);
      const y = TILE - h;
      ctx.fillStyle = rgb(220, 110, 30);
      ctx.fillRect(x, y, 2, h);
    }
    // mid flame: yellow
    for (let i = 0; i < 22; i++) {
      const x = (rnd() * TILE) | 0;
      const h = 4 + ((rnd() * 8) | 0);
      const y = TILE - h;
      ctx.fillStyle = rgb(245, 195, 50);
      ctx.fillRect(x, y, 2, h);
    }
    // hot core: white-yellow
    for (let i = 0; i < 12; i++) {
      const x = (rnd() * TILE) | 0;
      const h = 3 + ((rnd() * 5) | 0);
      const y = TILE - h;
      ctx.fillStyle = rgb(252, 235, 150);
      ctx.fillRect(x, y, 1, h);
    }
    // top two rows stay transparent so the slab doesn't visually clip
  },
  [T.FIRE_1]: (ctx, rnd) => {
    PAINTERS[T.FIRE_0](ctx, rnd);
    // shift the flame slightly: the second frame of vanilla's 2-frame loop
    ctx.clearRect(0, 0, TILE, 2);
    for (let i = 0; i < 14; i++) {
      const x = (rnd() * TILE) | 0;
      ctx.fillStyle = rgb(220, 110, 30);
      ctx.fillRect(x, TILE - 4, 2, 4);
    }
  },
  [T.COBWEB]: (ctx) => {
    ctx.clearRect(0, 0, TILE, TILE);
    ctx.strokeStyle = rgb(236, 240, 244, 0.95);
    ctx.lineWidth = 1;
    ctx.beginPath();
    for (const [a, b, c, d] of [[0, 0, 32, 32], [32, 0, 0, 32], [16, 0, 16, 32], [0, 16, 32, 16]]) { ctx.moveTo(a, b); ctx.lineTo(c, d); }
    for (const r of [5, 10, 15]) ctx.rect(16 - r, 16 - r, r * 2, r * 2);
    ctx.stroke();
  },
  [T.RAIL]: (ctx) => {
    ctx.clearRect(0, 0, TILE, TILE);
    ctx.fillStyle = rgb(120, 86, 48);
    for (let y = 2; y < TILE; y += 8) ctx.fillRect(3, y, 26, 3);
    ctx.fillStyle = rgb(150, 150, 150);
    ctx.fillRect(7, 0, 3, TILE);
    ctx.fillRect(22, 0, 3, TILE);
  },
  [T.RAIL_CORNER]: (ctx) => {
    // quarter arcs about the tile's south-east corner (joins the south and east edges), ties along the radii
    ctx.clearRect(0, 0, TILE, TILE);
    const arc = (r: number, w: number, color: string): void => {
      ctx.strokeStyle = color; ctx.lineWidth = w;
      ctx.beginPath(); ctx.arc(TILE, TILE, r, Math.PI, Math.PI * 1.5); ctx.stroke();
    };
    ctx.strokeStyle = rgb(120, 86, 48); ctx.lineWidth = 3;
    for (let k = 0; k < 4; k++) {
      const a = Math.PI * (1.04 + k * 0.14);
      ctx.beginPath(); ctx.moveTo(TILE + Math.cos(a) * 5, TILE + Math.sin(a) * 5); ctx.lineTo(TILE + Math.cos(a) * 27, TILE + Math.sin(a) * 27); ctx.stroke();
    }
    arc(8.5, 3, rgb(150, 150, 150)); arc(23.5, 3, rgb(150, 150, 150));
  },
  [T.POWERED_RAIL_ON]: (ctx) => {
    ctx.clearRect(0, 0, TILE, TILE);
    ctx.fillStyle = rgb(120, 86, 48);
    for (let y = 2; y < TILE; y += 8) ctx.fillRect(3, y, 26, 3);
    ctx.fillStyle = rgb(214, 176, 62);
    ctx.fillRect(7, 0, 3, TILE);
    ctx.fillRect(22, 0, 3, TILE);
    ctx.fillStyle = rgb(210, 40, 32);
    ctx.fillRect(15, 0, 2, TILE);
  },
  [T.DIRT_PATH_TOP]: (ctx, rnd) => speckle(ctx, rnd, [148, 122, 65], 16),
  [T.DIRT_PATH_SIDE]: (ctx, rnd) => {
    PAINTERS[T.DIRT](ctx, rnd);
    ctx.fillStyle = rgb(148, 122, 65);
    ctx.fillRect(0, 0, TILE, 3);
  },
  [T.FARMLAND]: (ctx, rnd) => {
    speckle(ctx, rnd, [82, 52, 28], 14);
    ctx.fillStyle = rgb(60, 36, 18);
    for (let x = 2; x < TILE; x += 8) ctx.fillRect(x, 0, 3, TILE);
  },
  [T.WHEAT]: (ctx, rnd) => {
    ctx.clearRect(0, 0, TILE, TILE);
    for (let i = 0; i < 9; i++) {
      const x = 2 + i * 3 + ((rnd() * 2) | 0), h = 20 + ((rnd() * 8) | 0);
      ctx.fillStyle = rgb(170, 150, 50);
      ctx.fillRect(x, TILE - h, 1, h);
      ctx.fillStyle = rgb(220, 190, 70);
      ctx.fillRect(x - 1, TILE - h - 3, 3, 7);
    }
  },
  [T.OAK_DOOR_BOTTOM]: (ctx, rnd) => doorPanel(ctx, rnd, [162, 130, 78], false),
  [T.OAK_DOOR_TOP]: (ctx, rnd) => doorPanel(ctx, rnd, [162, 130, 78], true),
  [T.SPRUCE_DOOR_BOTTOM]: (ctx, rnd) => doorPanel(ctx, rnd, [114, 84, 48], false),
  [T.SPRUCE_DOOR_TOP]: (ctx, rnd) => doorPanel(ctx, rnd, [114, 84, 48], true),
};

Object.assign(PAINTERS, utilityPainters()); // containers and utility blocks (render/utilityTextures.ts)

interface TopFlower { r: number; petal: string; hi: string; core: string }

function doorPanel(ctx: CanvasRenderingContext2D, rnd: () => number, base: [number, number, number], top: boolean): void {
  const [r, g, b] = base;
  ctx.clearRect(0, 0, TILE, TILE);
  ctx.fillStyle = rgb(r, g, b);
  ctx.fillRect(0, 0, TILE, TILE);
  for (let i = 0; i < 160; i++) {
    const d = (rnd() - 0.5) * 26;
    ctx.fillStyle = rgb(r + d, g + d, b + d);
    ctx.fillRect((rnd() * TILE) | 0, (rnd() * TILE) | 0, 1 + ((rnd() * 2) | 0), 1);
  }
  // frame
  ctx.fillStyle = rgb(r - 50, g - 46, b - 34);
  ctx.fillRect(0, 0, TILE, 1); ctx.fillRect(0, TILE - 1, TILE, 1);
  ctx.fillRect(0, 0, 1, TILE); ctx.fillRect(TILE - 1, 0, 1, TILE);
  // two recessed panels / window
  const mid = rgb(r - 26, g - 22, b - 16);
  ctx.fillStyle = mid;
  if (top) {
    ctx.fillRect(5, 5, TILE - 10, 9);
    ctx.fillRect(5, 18, TILE - 10, 9);
    // 2x2 window panes in the upper recess
    ctx.fillStyle = rgb(190, 220, 230, 0.85);
    ctx.fillRect(6, 6, TILE - 12, 7);
    ctx.fillStyle = rgb(r - 50, g - 46, b - 34);
    ctx.fillRect(15, 6, 2, 7); ctx.fillRect(6, 9, TILE - 12, 1);
  } else {
    ctx.fillRect(5, 5, TILE - 10, 10);
    ctx.fillRect(5, 19, TILE - 10, 8);
  }
}

/** Small 5-petal flowers on a jittered grid with leaf bits under them (top view). */
function scatterFlowers(
  ctx: CanvasRenderingContext2D, rnd: () => number, leavesPerFlower: number, style: (i: number) => TopFlower
): void {
  const spots: [number, number][] = [];
  for (let gy = 0; gy < 3; gy++) {
    for (let gx = 0; gx < 3; gx++) {
      const x = 5 + gx * 10 + ((rnd() * 4) | 0);
      const y = 5 + gy * 10 + ((rnd() * 4) | 0);
      if (rnd() < 0.22) continue; // leave gaps so the ground shows through
      spots.push([x, y]);
    }
  }
  // stems / leaves first, flowers on top
  for (const [x, y] of spots) {
    for (let k = 0; k < leavesPerFlower; k++) {
      const v = (rnd() * 40) | 0;
      ctx.fillStyle = rgb(62 + v, 118 + v, 44 + v * 0.5);
      const lx = x + (((rnd() - 0.5) * 9) | 0);
      const ly = y + (((rnd() - 0.5) * 9) | 0);
      if (rnd() < 0.5) ctx.fillRect(lx, ly, 3, 1); else ctx.fillRect(lx, ly, 1, 3);
    }
  }
  spots.forEach(([x, y], i) => {
    const f = style(i);
    const spin = rnd() * Math.PI * 2;
    for (let k = 0; k < 5; k++) {
      const a = spin + (k / 5) * Math.PI * 2;
      const fx = Math.round(x + Math.cos(a) * f.r);
      const fy = Math.round(y + Math.sin(a) * f.r);
      ctx.fillStyle = f.petal;
      ctx.fillRect(fx, fy, 2, 2);
      px(ctx, fx, fy, f.hi);
    }
    ctx.fillStyle = f.core;
    ctx.fillRect(x, y, 2, 2);
  });
}

// --- official Minecraft textures ---------------------------------------------
// Tile -> texture file name inside `public/mc/` (populated by
// scripts/extract-minecraft-textures.mjs from a local Minecraft install or
// downloaded from the mcasset.cloud asset dump).
const MC_TILE_FILES: Record<number, string> = {
  // terrain
  [T.GRASS_TOP]: 'grass_block_top',    // grayscale — tinted by the mesher
  [T.GRASS_SIDE]: 'grass_block_side',  // pre-colored in vanilla
  [T.DIRT]: 'dirt',
  [T.STONE]: 'stone',
  [T.COBBLE]: 'cobblestone',
  [T.SAND]: 'sand',
  [T.LOG_SIDE]: 'oak_log',
  [T.LOG_TOP]: 'oak_log_top',
  [T.LEAVES]: 'oak_leaves',            // grayscale — tinted by the mesher
  [T.PLANKS]: 'oak_planks',
  [T.GLASS]: 'glass',
  [T.WATER]: 'water_still',            // grayscale — fixed multiply tint below
  [T.SNOW]: 'snow',
  [T.SNOW_SIDE]: 'grass_block_snow',
  [T.GRAVEL]: 'gravel',
  [T.BEDROCK]: 'bedrock',
  [T.COAL]: 'coal_ore',
  [T.IRON]: 'iron_ore',
  [T.GOLD]: 'gold_ore',
  [T.DIAMOND]: 'diamond_ore',
  [T.GLOWSTONE]: 'glowstone',
  [T.TALL_GRASS]: 'short_grass',       // grayscale — tinted by the mesher
  [T.POPPY]: 'poppy',
  [T.CACTUS_SIDE]: 'cactus_side',
  [T.CACTUS_TOP]: 'cactus_top',
  [T.STONE_BRICK]: 'stone_bricks',
  [T.LAVA]: 'lava_still',
  [T.TORCH]: 'torch',
  [T.PODZOL_TOP]: 'podzol_top',        // grayscale — tinted by the mesher
  [T.PODZOL_SIDE]: 'podzol_side',
  [T.MYCELIUM_TOP]: 'mycelium_top',
  [T.MYCELIUM_SIDE]: 'mycelium_side',
  [T.COARSE_DIRT]: 'coarse_dirt',
  [T.ROOTED_DIRT]: 'rooted_dirt',
  [T.MOSSY_COBBLE]: 'mossy_cobblestone',
  [T.SMOOTH_STONE]: 'smooth_stone',
  [T.MOSSY_BRICKS]: 'mossy_stone_bricks',
  [T.CRACKED_BRICKS]: 'cracked_stone_bricks',
  [T.CHISELED_BRICKS]: 'chiseled_stone_bricks',
  [T.BRICKS]: 'bricks',
  [T.GRANITE]: 'granite',
  [T.DIORITE]: 'diorite',
  [T.ANDESITE]: 'andesite',
  [T.DEEPSLATE]: 'deepslate',
  [T.DEEPSLATE_TOP]: 'deepslate_top',
  [T.COBBLED_DEEPSLATE]: 'cobbled_deepslate',
  [T.TUFF]: 'tuff',
  [T.CALCITE]: 'calcite',
  [T.DRIPSTONE]: 'dripstone_block',
  [T.BASALT_TOP]: 'basalt_top',
  [T.BASALT_SIDE]: 'basalt_side',
  [T.SANDSTONE_TOP]: 'sandstone_top',
  [T.SANDSTONE]: 'sandstone',
  [T.SANDSTONE_BOTTOM]: 'sandstone_bottom',
  [T.RED_SANDSTONE_TOP]: 'red_sandstone_top',
  [T.RED_SANDSTONE]: 'red_sandstone',
  [T.RED_SANDSTONE_BOTTOM]: 'red_sandstone_bottom',
  [T.CLAY]: 'clay',
  [T.TERRACOTTA]: 'terracotta',
  [T.OBSIDIAN]: 'obsidian',
  [T.CRYING_OBSIDIAN]: 'crying_obsidian',
  [T.ICE]: 'ice',
  [T.PACKED_ICE]: 'packed_ice',
  [T.BLUE_ICE]: 'blue_ice',
  [T.MAGMA]: 'magma',
  [T.SEA_LANTERN]: 'sea_lantern',
  [T.SHROOMLIGHT]: 'shroomlight',
  [T.NETHERRACK]: 'netherrack',
  // nether dimension (stems/portal are animation strips: first frame is used)
  [T.BLACKSTONE]: 'blackstone', [T.BLACKSTONE_TOP]: 'blackstone_top',
  [T.CRIMSON_NYLIUM]: 'crimson_nylium', [T.CRIMSON_NYLIUM_SIDE]: 'crimson_nylium_side',
  [T.WARPED_NYLIUM]: 'warped_nylium', [T.WARPED_NYLIUM_SIDE]: 'warped_nylium_side',
  [T.CRIMSON_STEM]: 'crimson_stem', [T.CRIMSON_STEM_TOP]: 'crimson_stem_top',
  [T.WARPED_STEM]: 'warped_stem', [T.WARPED_STEM_TOP]: 'warped_stem_top',
  [T.NETHER_WART_BLOCK]: 'nether_wart_block', [T.WARPED_WART_BLOCK]: 'warped_wart_block',
  [T.NETHER_PORTAL]: 'nether_portal', [T.NETHER_GOLD_ORE]: 'nether_gold_ore',
  [T.NETHER_WART_0]: 'nether_wart_stage0', [T.NETHER_WART_1]: 'nether_wart_stage1', [T.NETHER_WART_2]: 'nether_wart_stage2',
  [T.CRIMSON_FUNGUS]: 'crimson_fungus', [T.WARPED_FUNGUS]: 'warped_fungus',
  [T.CRIMSON_ROOTS]: 'crimson_roots', [T.WARPED_ROOTS]: 'warped_roots',
  [T.WEEPING_VINES]: 'weeping_vines', [T.WEEPING_VINES_PLANT]: 'weeping_vines_plant',
  [T.TWISTING_VINES]: 'twisting_vines', [T.TWISTING_VINES_PLANT]: 'twisting_vines_plant',
  [T.SOUL_FIRE]: 'soul_fire_0', [T.SPAWNER]: 'spawner',
  [T.CHISELED_NETHER_BRICKS]: 'chiseled_nether_bricks', [T.CRACKED_NETHER_BRICKS]: 'cracked_nether_bricks',
  [T.SOUL_SAND]: 'soul_sand',
  [T.SOUL_SOIL]: 'soul_soil',
  [T.NETHER_BRICKS]: 'nether_bricks',
  [T.QUARTZ_ORE]: 'nether_quartz_ore',
  [T.ANCIENT_DEBRIS_SIDE]: 'ancient_debris_side',
  [T.ANCIENT_DEBRIS_TOP]: 'ancient_debris_top',
  [T.COPPER_ORE]: 'copper_ore',
  [T.REDSTONE_ORE]: 'redstone_ore',
  [T.LAPIS_ORE]: 'lapis_ore',
  [T.EMERALD_ORE]: 'emerald_ore',
  [T.DEEPSLATE_COAL_ORE]: 'deepslate_coal_ore',
  [T.DEEPSLATE_IRON_ORE]: 'deepslate_iron_ore',
  [T.DEEPSLATE_COPPER_ORE]: 'deepslate_copper_ore',
  [T.DEEPSLATE_GOLD_ORE]: 'deepslate_gold_ore',
  [T.DEEPSLATE_REDSTONE_ORE]: 'deepslate_redstone_ore',
  [T.DEEPSLATE_LAPIS_ORE]: 'deepslate_lapis_ore',
  [T.DEEPSLATE_EMERALD_ORE]: 'deepslate_emerald_ore',
  [T.DEEPSLATE_DIAMOND_ORE]: 'deepslate_diamond_ore',
  // wood species
  [T.SPRUCE_PLANKS]: 'spruce_planks',
  [T.SPRUCE_LOG]: 'spruce_log',
  [T.SPRUCE_LOG_TOP]: 'spruce_log_top',
  [T.SPRUCE_LEAVES]: 'spruce_leaves',
  [T.BIRCH_PLANKS]: 'birch_planks',
  [T.BIRCH_LOG]: 'birch_log',
  [T.BIRCH_LOG_TOP]: 'birch_log_top',
  [T.BIRCH_LEAVES]: 'birch_leaves',
  [T.JUNGLE_PLANKS]: 'jungle_planks',
  [T.JUNGLE_LOG]: 'jungle_log',
  [T.JUNGLE_LOG_TOP]: 'jungle_log_top',
  [T.JUNGLE_LEAVES]: 'jungle_leaves',
  [T.ACACIA_PLANKS]: 'acacia_planks',
  [T.ACACIA_LOG]: 'acacia_log',
  [T.ACACIA_LOG_TOP]: 'acacia_log_top',
  [T.ACACIA_LEAVES]: 'acacia_leaves',
  [T.DARK_OAK_PLANKS]: 'dark_oak_planks',
  [T.DARK_OAK_LOG]: 'dark_oak_log',
  [T.DARK_OAK_LOG_TOP]: 'dark_oak_log_top',
  [T.DARK_OAK_LEAVES]: 'dark_oak_leaves',
  [T.MANGROVE_PLANKS]: 'mangrove_planks',
  [T.MANGROVE_LOG]: 'mangrove_log',
  [T.MANGROVE_LOG_TOP]: 'mangrove_log_top',
  [T.MANGROVE_LEAVES]: 'mangrove_leaves',
  [T.CHERRY_PLANKS]: 'cherry_planks',
  [T.CHERRY_LOG]: 'cherry_log',
  [T.CHERRY_LOG_TOP]: 'cherry_log_top',
  [T.CHERRY_LEAVES]: 'cherry_leaves',
  [T.PALE_OAK_PLANKS]: 'pale_oak_planks',
  [T.PALE_OAK_LOG]: 'pale_oak_log',
  [T.PALE_OAK_LOG_TOP]: 'pale_oak_log_top',
  [T.PALE_OAK_LEAVES]: 'pale_oak_leaves',
  // misc
  [T.BOOKSHELF]: 'bookshelf',
  [T.CRAFTING_TABLE_TOP]: 'crafting_table_top',
  [T.CRAFTING_TABLE_SIDE]: 'crafting_table_side',
  [T.FURNACE_TOP]: 'furnace_top',
  [T.FURNACE_SIDE]: 'furnace_side',
  [T.FURNACE_FRONT]: 'furnace_front',
  [T.TNT_TOP]: 'tnt_top',
  [T.TNT_SIDE]: 'tnt_side',
  [T.TNT_BOTTOM]: 'tnt_bottom',
  [T.PUMPKIN_TOP]: 'pumpkin_top',
  [T.PUMPKIN_SIDE]: 'pumpkin_side',
  [T.MELON_TOP]: 'melon_top',
  [T.MELON_SIDE]: 'melon_side',
  [T.HAY_TOP]: 'hay_block_top',
  [T.HAY_SIDE]: 'hay_block_side',
  [T.SOUL_TORCH]: 'soul_torch',
  // flora
  [T.DANDELION]: 'dandelion',
  [T.BLUE_ORCHID]: 'blue_orchid',
  [T.ALLIUM]: 'allium',
  [T.AZURE_BLUET]: 'azure_bluet',
  [T.TULIP_RED]: 'red_tulip',
  [T.TULIP_ORANGE]: 'orange_tulip',
  [T.TULIP_WHITE]: 'white_tulip',
  [T.TULIP_PINK]: 'pink_tulip',
  [T.OXEYE_DAISY]: 'oxeye_daisy',
  [T.CORNFLOWER]: 'cornflower',
  [T.LILY_OF_THE_VALLEY]: 'lily_of_the_valley',
  [T.DEAD_BUSH]: 'dead_bush',          // pre-colored in modern versions
  [T.MUSHROOM_RED]: 'red_mushroom',
  [T.MUSHROOM_BROWN]: 'brown_mushroom',
  [T.OAK_SAPLING]: 'oak_sapling',
  [T.FERN]: 'fern',                    // grayscale — tinted by the mesher
  // stone / deepslate variants
  [T.POLISHED_GRANITE]: 'polished_granite',
  [T.POLISHED_DIORITE]: 'polished_diorite',
  [T.POLISHED_ANDESITE]: 'polished_andesite',
  [T.POLISHED_DEEPSLATE]: 'polished_deepslate',
  [T.DEEPSLATE_BRICKS]: 'deepslate_bricks',
  [T.DEEPSLATE_TILES]: 'deepslate_tiles',
  [T.CHISELED_DEEPSLATE]: 'chiseled_deepslate',
  // sandstone variants
  [T.SMOOTH_SANDSTONE]: 'smooth_sandstone',
  [T.CUT_SANDSTONE]: 'cut_sandstone',
  [T.CHISELED_SANDSTONE]: 'chiseled_sandstone',
  [T.SMOOTH_RED_SANDSTONE]: 'smooth_red_sandstone',
  [T.CUT_RED_SANDSTONE]: 'cut_red_sandstone',
  [T.CHISELED_RED_SANDSTONE]: 'chiseled_red_sandstone',
  // soils & organics
  [T.RED_SAND]: 'red_sand',
  [T.MUD]: 'mud',
  [T.PACKED_MUD]: 'packed_mud',
  [T.MUD_BRICKS]: 'mud_bricks',
  [T.MOSS]: 'moss_block',
  // metals & minerals
  [T.COPPER_BLOCK]: 'copper_block',
  [T.CUT_COPPER]: 'cut_copper',
  [T.EXPOSED_COPPER]: 'exposed_copper',
  [T.WEATHERED_COPPER]: 'weathered_copper',
  [T.OXIDIZED_COPPER]: 'oxidized_copper',
  [T.RAW_IRON]: 'raw_iron_block',
  [T.RAW_COPPER]: 'raw_copper_block',
  [T.RAW_GOLD]: 'raw_gold_block',
  [T.IRON_BLOCK]: 'iron_block',
  [T.GOLD_BLOCK]: 'gold_block',
  [T.DIAMOND_BLOCK]: 'diamond_block',
  [T.EMERALD_BLOCK]: 'emerald_block',
  [T.LAPIS_BLOCK]: 'lapis_block',
  [T.REDSTONE_BLOCK]: 'redstone_block',
  [T.COAL_BLOCK]: 'coal_block',
  [T.AMETHYST]: 'amethyst_block',
  // ocean & misc
  [T.PRISMARINE]: 'prismarine',
  [T.PRISMARINE_BRICKS]: 'prismarine_bricks',
  [T.DARK_PRISMARINE]: 'dark_prismarine',
  [T.BONE_SIDE]: 'bone_block_side',
  [T.BONE_TOP]: 'bone_block_top',
  [T.DRIED_KELP]: 'dried_kelp_block',
  [T.SPONGE]: 'sponge',
  [T.QUARTZ_SIDE]: 'quartz_block_side',
  [T.QUARTZ_TOP]: 'quartz_block_top',
  [T.JACK_O_LANTERN]: 'jack_o_lantern',
  // flora
  [T.SUNFLOWER]: 'sunflower_front',
  [T.ROSE_BUSH]: 'rose_bush_top',
  [T.LILAC]: 'lilac_top',
  [T.PEONY]: 'peony_top',
  [T.AZALEA_LEAVES]: 'azalea_leaves',
  [T.FLOWERING_AZALEA_LEAVES]: 'flowering_azalea_leaves',
  [T.AZALEA]: 'azalea_plant',
  [T.FLOWERING_AZALEA]: 'flowering_azalea_plant',
  // ground cover (pink petals / wildflowers / leaf litter use custom vanilla
  // models, so those stay procedural)
  [T.LILY_PAD]: 'lily_pad',              // grayscale — tinted by the mesher
  // worldgen features & structures (animated strips use their first frame)
  [T.KELP]: 'kelp',
  [T.KELP_PLANT]: 'kelp_plant',
  [T.SEAGRASS]: 'seagrass',
  [T.SUGAR_CANE]: 'sugar_cane',
  [T.SWEET_BERRY_BUSH]: 'sweet_berry_bush_stage3',
  [T.RED_MUSHROOM_BLOCK]: 'red_mushroom_block',
  [T.BROWN_MUSHROOM_BLOCK]: 'brown_mushroom_block',
  [T.MUSHROOM_STEM]: 'mushroom_stem',
  [T.SCULK]: 'sculk',
  [T.CAVE_VINES]: 'cave_vines_lit',
  [T.DRIPSTONE_UP]: 'pointed_dripstone_up_tip',
  [T.DRIPSTONE_DOWN]: 'pointed_dripstone_down_tip',
  [T.SMOOTH_BASALT]: 'smooth_basalt',
  [T.POWDER_SNOW]: 'powder_snow',
  [T.CORAL_TUBE]: 'tube_coral_block',
  [T.CORAL_BRAIN]: 'brain_coral_block',
  [T.CORAL_BUBBLE]: 'bubble_coral_block',
  [T.CORAL_FIRE]: 'fire_coral_block',
  [T.CORAL_HORN]: 'horn_coral_block',
  [T.FIRE_0]: 'fire_0',
  [T.FIRE_1]: 'fire_1',
  [T.ENCHANTING_TABLE_TOP]: 'enchanting_table_top',
  [T.ENCHANTING_TABLE_SIDE]: 'enchanting_table_side',
  [T.ENCHANTING_TABLE_BOTTOM]: 'enchanting_table_bottom',
  [T.ANVIL]: 'anvil',
  [T.ANVIL_TOP]: 'anvil_top',
  [T.CHIPPED_ANVIL_TOP]: 'chipped_anvil_top',
  [T.DAMAGED_ANVIL_TOP]: 'damaged_anvil_top',
  [T.SMITHING_TABLE_TOP]: 'smithing_table_top',
  [T.SMITHING_TABLE_SIDE]: 'smithing_table_side',
  [T.SMITHING_TABLE_FRONT]: 'smithing_table_front',
  [T.SMITHING_TABLE_BOTTOM]: 'smithing_table_bottom',
  [T.COMPOSTER_TOP]: 'composter_top',
  [T.COMPOSTER_SIDE]: 'composter_side',
  [T.COMPOSTER_BOTTOM]: 'composter_bottom',
  [T.LECTERN_TOP]: 'lectern_top',
  [T.LECTERN_SIDES]: 'lectern_sides',
  [T.LECTERN_FRONT]: 'lectern_front',
  [T.LECTERN_BASE]: 'lectern_base',
  [T.BREWING_STAND]: 'item/brewing_stand', // the item sprite doubles as the block's cross model
  [T.BARREL_TOP]: 'barrel_top',
  [T.BARREL_SIDE]: 'barrel_side',
  [T.BARREL_BOTTOM]: 'barrel_bottom',
  [T.FLETCHING_TABLE_TOP]: 'fletching_table_top',
  [T.FLETCHING_TABLE_SIDE]: 'fletching_table_side',
  [T.FLETCHING_TABLE_FRONT]: 'fletching_table_front',
  [T.GRINDSTONE_SIDE]: 'grindstone_side',
  [T.GRINDSTONE_ROUND]: 'grindstone_round',
  [T.GRINDSTONE_PIVOT]: 'grindstone_pivot',
  [T.SMOKER_TOP]: 'smoker_top',
  [T.SMOKER_SIDE]: 'smoker_side',
  [T.SMOKER_FRONT]: 'smoker_front',
  [T.SMOKER_BOTTOM]: 'smoker_bottom',
  [T.BLAST_FURNACE_TOP]: 'blast_furnace_top',
  [T.BLAST_FURNACE_SIDE]: 'blast_furnace_side',
  [T.BLAST_FURNACE_FRONT]: 'blast_furnace_front',
  // lit fronts: the unlit art with its dark opening repainted as glowing coals (see glowOpening)
  [T.FURNACE_FRONT_ON]: 'furnace_front_on',
  [T.SMOKER_FRONT_ON]: 'smoker_front_on',
  [T.BLAST_FURNACE_FRONT_ON]: 'blast_furnace_front_on',
  [T.STONECUTTER_TOP]: 'stonecutter_top',
  [T.STONECUTTER_SIDE]: 'stonecutter_side',
  [T.STONECUTTER_BOTTOM]: 'stonecutter_bottom',
  [T.END_STONE]: 'end_stone', [T.END_STONE_BRICKS]: 'end_stone_bricks',
  [T.END_FRAME_TOP]: 'end_portal_frame_top', [T.END_FRAME_SIDE]: 'end_portal_frame_side',
  [T.END_FRAME_EYE]: 'end_portal_frame_eye', [T.DRAGON_EGG]: 'dragon_egg',
  [T.COBWEB]: 'cobweb',
  [T.IRON_BARS]: 'iron_bars', [T.POLISHED_BLACKSTONE_BRICKS]: 'polished_blackstone_bricks', [T.GILDED_BLACKSTONE]: 'gilded_blackstone',
  [T.TRIAL_SPAWNER]: 'trial_spawner_side_inactive', [T.VAULT]: 'vault_side_off',
  [T.CHORUS_PLANT]: 'chorus_plant', [T.CHORUS_FLOWER]: 'chorus_flower', [T.PURPUR]: 'purpur_block', [T.PURPUR_PILLAR]: 'purpur_pillar',
  [T.RAIL]: 'rail',
  [T.RAIL_CORNER]: 'rail_corner', [T.POWERED_RAIL_ON]: 'powered_rail_on',
  [T.DIRT_PATH_TOP]: 'dirt_path_top',
  [T.DIRT_PATH_SIDE]: 'dirt_path_side',
  [T.FARMLAND]: 'farmland_moist',
  [T.FARMLAND_DRY]: 'farmland',
  [T.WHEAT]: 'wheat_stage7',
  ...Object.fromEntries(CROP_TILES.wheat.map((tile, i) => [tile, `wheat_stage${i}`])),
  ...Object.fromEntries(CROP_TILES.carrots.map((tile, i) => [tile, `carrots_stage${i}`])),
  ...Object.fromEntries(CROP_TILES.potatoes.map((tile, i) => [tile, `potatoes_stage${i}`])),
  ...Object.fromEntries(CROP_TILES.beetroots.map((tile, i) => [tile, `beetroots_stage${i}`])),
  [T.TRAPDOOR_0]: 'oak_trapdoor',
  [T.TRAPDOOR_1]: 'spruce_trapdoor',
  [T.TRAPDOOR_2]: 'birch_trapdoor',
  [T.TRAPDOOR_3]: 'jungle_trapdoor',
  [T.TRAPDOOR_4]: 'acacia_trapdoor',
  [T.TRAPDOOR_5]: 'dark_oak_trapdoor',
  [T.TRAPDOOR_6]: 'mangrove_trapdoor',
  [T.TRAPDOOR_7]: 'cherry_trapdoor',
  [T.TRAPDOOR_8]: 'pale_oak_trapdoor',
  [T.OAK_DOOR_BOTTOM]: 'oak_door_bottom',
  [T.OAK_DOOR_TOP]: 'oak_door_top',
  [T.SPRUCE_DOOR_BOTTOM]: 'spruce_door_bottom',
  [T.SPRUCE_DOOR_TOP]: 'spruce_door_top',
  // beds are entity-rendered in vanilla (entity/bed/*.png UV sheets), so the
  // bed tiles stay procedural
};

/** Tiles whose MC texture carries transparent texels (vanilla draws them on
 *  15/16-tall blocks, e.g. the dirt path). This engine renders full opaque
 *  cubes, so those texels would show as black — draw a base texture under
 *  them instead.
 *
 *  The dirt path's missing top row is the empty space above the shorter
 *  block, so it must be filled with the path's own top colour — not the
 *  grass strip of grass_block_side, which painted a green 1-px edge around
 *  every path face. The procedural fallback painter tops the side with the
 *  same tan, so both paths agree. */
const MC_TILE_BASES: Record<number, string> = {
  [T.DIRT_PATH_SIDE]: 'dirt_path_top',
};

// 16-color sets (terracotta / concrete / wool) share the vanilla file naming.
Object.assign(MC_TILE_FILES, utilityTileFiles());
for (const c of COLOR_KEYS) {
  MC_TILE_FILES[COLOR_TILES[c + '_terracotta']] = c + '_terracotta';
  MC_TILE_FILES[COLOR_TILES[c + '_concrete']] = c + '_concrete';
  MC_TILE_FILES[COLOR_TILES[c + '_wool']] = c + '_wool';
}

/**
 * One entity-model face tile (entityModels.ts FACE_TILES): the face's rect of
 * its 64x64 sheet, painted at the tile's top-left at the usual 2px per texel —
 * the baked quads' UVs index exactly that corner. The atlas is mipmapped and
 * the gutter wraps each tile's edges, so the rest of the tile is filled by
 * replicating the face's edge texels outwards rather than left transparent.
 * Without the extracted sheet (public/mc is gitignored) the face is a flat
 * stand-in colour so the model still reads.
 */
function paintEntityFace(octx: CanvasRenderingContext2D, img: HTMLImageElement | null, face: FaceTile): void {
  const s = TILE / FACE_TILE_TEXELS;
  const [sx, sy, sw, sh] = face.rect;
  const w = sw * s, h = sh * s;
  octx.imageSmoothingEnabled = false;
  if (!img) {
    octx.fillStyle = face.sheet.includes('bed') ? 'rgb(160, 40, 40)' : 'rgb(150, 105, 45)';
    octx.fillRect(0, 0, TILE, TILE);
    return;
  }
  octx.drawImage(img, sx, sy, sw, sh, 0, 0, w, h);
  // edge replicate: right column, then the bottom row (which then carries the corner)
  if (w < TILE) octx.drawImage(octx.canvas, w - 1, 0, 1, h, w, 0, TILE - w, h);
  if (h < TILE) octx.drawImage(octx.canvas, 0, h - 1, TILE, 1, 0, h, TILE, TILE - h);
}

/** Fixed multiply tint for grayscale vanilla textures (biome-tint stand-in). */
const TILE_TINTS: Record<number, [number, number, number]> = {
  [T.WATER]: [68 / 255, 175 / 255, 245 / 255], // Bedrock default water 0x44AFF5
};

/**
 * Fire tiles in the atlas are only sampled by particles (the block itself
 * renders from the animated fire frame array — see render/fire.ts). Vanilla's
 * sheet is a 16xN animation strip, so draw its fullest frame rather than the
 * top one, and key the black backdrop out: dumps of this sheet are often plain
 * RGB with an opaque black background, which would leave a black square and
 * give ignition particles black chips.
 */

function drawFireFrame(ctx: CanvasRenderingContext2D, strip: HTMLImageElement): void {
  const w = strip.naturalWidth;
  const frames = w > 0 ? Math.floor(strip.naturalHeight / w) : 0;
  if (frames < 1) return;
  const scratch = document.createElement('canvas');
  scratch.width = w;
  scratch.height = strip.naturalHeight;
  const sctx = scratch.getContext('2d', { willReadFrequently: true });
  if (!sctx) return;
  sctx.drawImage(strip, 0, 0);
  const src = sctx.getImageData(0, 0, w, strip.naturalHeight).data;

  /** Visible (non-background) texels in one frame — the fullest one wins. */
  const solid = (f: number): number => {
    let n = 0;
    for (let i = f * w * w; i < (f + 1) * w * w; i++) {
      if (Math.max(src[i * 4], src[i * 4 + 1], src[i * 4 + 2]) > 8) n++;
    }
    return n;
  };
  let best = 0;
  for (let f = 1; f < frames; f++) if (solid(f) > solid(best)) best = f;

  ctx.imageSmoothingEnabled = false;
  ctx.drawImage(strip, 0, best * w, w, w, 0, 0, TILE, TILE);
  const tile = ctx.getImageData(0, 0, TILE, TILE);
  const d = tile.data;
  for (let i = 0; i < TILE * TILE; i++) {
    if (Math.max(d[i * 4], d[i * 4 + 1], d[i * 4 + 2]) <= 8) d[i * 4 + 3] = 0;
  }
  ctx.putImageData(tile, 0, 0);
}

// redstone: official files only; a file that was not extracted shows the missing-texture marker
const RS_TILE_FILES: Record<number, string> = {
  [T.RS_DUST_DOT]: 'redstone_dust_dot', [T.RS_DUST_LINE]: 'redstone_dust_line0',
  [T.RS_TORCH]: 'redstone_torch', [T.RS_TORCH_OFF]: 'redstone_torch_off', [T.LEVER]: 'lever',
  [T.REPEATER]: 'repeater', [T.REPEATER_ON]: 'repeater_on', [T.COMPARATOR]: 'comparator', [T.COMPARATOR_ON]: 'comparator_on',
  [T.RS_LAMP]: 'redstone_lamp', [T.RS_LAMP_ON]: 'redstone_lamp_on',
  [T.OBSERVER_FRONT]: 'observer_front', [T.OBSERVER_BACK]: 'observer_back', [T.OBSERVER_BACK_ON]: 'observer_back_on',
  [T.OBSERVER_SIDE]: 'observer_side', [T.OBSERVER_TOP]: 'observer_top',
  [T.PISTON_TOP]: 'piston_top', [T.PISTON_TOP_STICKY]: 'piston_top_sticky', [T.PISTON_SIDE]: 'piston_side',
  [T.PISTON_BOTTOM]: 'piston_bottom', [T.PISTON_INNER]: 'piston_inner',
  [T.NOTE_BLOCK]: 'note_block', [T.TARGET_TOP]: 'target_top', [T.TARGET_SIDE]: 'target_side',
  [T.DAYLIGHT_TOP]: 'daylight_detector_top', [T.DAYLIGHT_INV_TOP]: 'daylight_detector_inverted_top', [T.DAYLIGHT_SIDE]: 'daylight_detector_side',
  [T.TRIPWIRE_HOOK]: 'tripwire_hook', [T.TRIPWIRE]: 'tripwire',
  [T.IRON_DOOR_BOTTOM]: 'iron_door_bottom', [T.IRON_DOOR_TOP]: 'iron_door_top', [T.IRON_TRAPDOOR]: 'iron_trapdoor',
  [T.POWERED_RAIL]: 'powered_rail', [T.DETECTOR_RAIL]: 'detector_rail', [T.DETECTOR_RAIL_ON]: 'detector_rail_on',
  [T.ACTIVATOR_RAIL]: 'activator_rail', [T.ACTIVATOR_RAIL_ON]: 'activator_rail_on',
};
Object.assign(MC_TILE_FILES, RS_TILE_FILES);
/** Vanilla's magenta/black missing-texture marker. */
const missingTile: Painter = (ctx) => {
  for (let y = 0; y < 2; y++) for (let x = 0; x < 2; x++) {
    ctx.fillStyle = (x + y) & 1 ? '#000000' : '#f800f8';
    ctx.fillRect(x * TILE / 2, y * TILE / 2, TILE / 2, TILE / 2);
  }
};
for (const t of Object.keys(RS_TILE_FILES)) PAINTERS[Number(t)] ??= missingTile;

async function loadMcTexture(name: string): Promise<HTMLImageElement | null> {
  const img = new Image();
  img.src = `mc/${name}.png`; // relative — works under vite's './' base
  try {
    await img.decode();
    return img;
  } catch {
    return null; // not extracted — procedural fallback
  }
}

function oreBlobs(ctx: CanvasRenderingContext2D, rnd: () => number, color: [number, number, number], blobs: number): void {
  for (let i = 0; i < blobs; i++) {
    const cx = 4 + ((rnd() * (TILE - 10)) | 0);
    const cy = 4 + ((rnd() * (TILE - 10)) | 0);
    const s = 3 + ((rnd() * 3) | 0);
    for (let dy = 0; dy < s; dy++) {
      for (let dx = 0; dx < s; dx++) {
        if (rnd() < 0.2) continue;
        const d = (rnd() - 0.5) * 30;
        px(ctx, cx + dx, cy + dy, rgb(color[0] + d, color[1] + d, color[2] + d));
      }
    }
  }
}

export interface AtlasResult {
  canvas: HTMLCanvasElement;
  /** Atlas-space UV of the tile content origin (top-left of the 32px area). */
  tileOriginU: Float32Array;
  tileOriginV: Float32Array;
  /** (tile size, cell size, gutter) in atlas UV units. */
  tileSizeU: number;
  tileSizeV: number;
  gutterU: number;
  gutterV: number;
}

export async function buildAtlas(): Promise<AtlasResult> {
  const canvas = document.createElement('canvas');
  canvas.width = ATLAS_W;
  canvas.height = ATLAS_H;
  const ctx = canvas.getContext('2d')!;
  ctx.imageSmoothingEnabled = false;

  // Preload the official textures (null where not extracted → fallback).
  const mcImages = new Map<number, HTMLImageElement>();
  const mcBaseImages = new Map<number, HTMLImageElement>();
  await Promise.all(
    Object.entries(MC_TILE_FILES).map(async ([tile, name]) => {
      const img = await loadMcTexture(name);
      if (img) mcImages.set(Number(tile), img);
    })
  );
  await Promise.all(
    Object.entries(MC_TILE_BASES).map(async ([tile, name]) => {
      const img = await loadMcTexture(name);
      if (img) mcBaseImages.set(Number(tile), img);
    })
  );
  // block-entity models sample whole 64x64 UV sheets (entityModels.ts)
  const sheets = new Map<string, HTMLImageElement | null>();
  for (const f of FACE_TILES) {
    if (!sheets.has(f.sheet)) sheets.set(f.sheet, await loadMcTexture(f.sheet));
  }

  const tileCount = ATLAS_COLS * ATLAS_ROWS;
  const tileOriginU = new Float32Array(tileCount);
  const tileOriginV = new Float32Array(tileCount);
  let officialCount = 0;

  // Scratch tile canvas: official texture (2x nearest) or procedural painter.
  const off = document.createElement('canvas');
  off.width = TILE;
  off.height = TILE;
  const octx = off.getContext('2d')!;

  for (let t = 0; t < tileCount; t++) {
    const col = t % ATLAS_COLS;
    const row = (t / ATLAS_COLS) | 0;
    const cx0 = col * CELL;
    const cy0 = row * CELL;

    octx.clearRect(0, 0, TILE, TILE);
    const mc = mcImages.get(t);
    if (mc) {
      officialCount++;
      octx.imageSmoothingEnabled = false;
      const base = mcBaseImages.get(t);
      if (base) octx.drawImage(base, 0, 0, 16, 16, 0, 0, TILE, TILE);
      // fire sheets are animation strips — take a full frame, not the first row
      if (t === T.FIRE_0 || t === T.FIRE_1) drawFireFrame(octx, mc);
      else octx.drawImage(mc, 0, 0, 16, 16, 0, 0, TILE, TILE);
      const tint = TILE_TINTS[t];
      if (tint) {
        octx.globalCompositeOperation = 'multiply';
        octx.fillStyle = rgb(tint[0] * 255, tint[1] * 255, tint[2] * 255);
        octx.fillRect(0, 0, TILE, TILE);
        octx.globalCompositeOperation = 'destination-in';
        octx.fillStyle = 'rgba(255,255,255,0.82)'; // vanilla-ish water opacity
        octx.fillRect(0, 0, TILE, TILE);
        octx.globalCompositeOperation = 'source-over';
      }
    } else if (t >= ENTITY_TILE_BASE && t < ENTITY_TILE_BASE + FACE_TILES.length) {
      const face = FACE_TILES[t - ENTITY_TILE_BASE];
      const sheet = sheets.get(face.sheet) ?? null;
      if (sheet) officialCount++;
      paintEntityFace(octx, sheet, face);
    } else {
      const painter = PAINTERS[t];
      if (!painter) continue;
      painter(octx, mulberry32(0x9e37 + t * 7919));
    }

    // Draw the tile at 9 wrapped offsets **inside a 3xTILE scratch canvas**,
    // where the ±TILE offsets are genuinely seamless, then blit only the
    // center CELL×CELL region (tile + its 2px wrapped gutter). Drawing the
    // offsets directly into the atlas would spill 30px of every copy into
    // neighbouring cells (a 2px gutter only needs 2px of wrap).
    const wrap = document.createElement('canvas');
    wrap.width = TILE * 3;
    wrap.height = TILE * 3;
    const wctx = wrap.getContext('2d')!;
    wctx.clearRect(0, 0, wrap.width, wrap.height);
    for (let dy = -TILE; dy <= TILE; dy += TILE) {
      for (let dx = -TILE; dx <= TILE; dx += TILE) {
        wctx.drawImage(off, TILE + dx, TILE + dy);
      }
    }
    ctx.drawImage(wrap, TILE - GUTTER, TILE - GUTTER, CELL, CELL, cx0, cy0, CELL, CELL);

    tileOriginU[t] = (cx0 + GUTTER) / ATLAS_W;
    tileOriginV[t] = (cy0 + GUTTER) / ATLAS_H;
  }

  if (officialCount > 0) {
    console.info(`atlas: ${officialCount} official Minecraft textures, ${tileCount - officialCount} procedural`);
  }

  return {
    canvas,
    tileOriginU,
    tileOriginV,
    tileSizeU: TILE / ATLAS_W,
    tileSizeV: TILE / ATLAS_H,
    gutterU: GUTTER / ATLAS_W,
    gutterV: GUTTER / ATLAS_H,
  };
}
